/**
 * auditReviewParts.jsx — chain UI + audit-action helpers shared by AuditAnswer
 * (per-answer review page) and the qualitative-tab accordion in AuditList.
 *
 * Originally lived inside AuditAnswer.jsx; extracted so the qualitative
 * accordion can render the same chain modal + reject panel + confirm dialog
 * without duplicating the markup.
 */
import React, { useEffect, useState } from "react";

/* ── Date helper ── */
export const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
};

/* ── Chain status palette ── */
const CHAIN_SC = {
  APPROVED:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Approved",  icon: "✓" },
  ACCEPTED:  { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Approved",  icon: "✓" },
  REJECTED:  { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Rejected",  icon: "✕" },
  PENDING:   { color: "#d97706", bg: "#fffbeb", border: "#fde68a", label: "Pending",   icon: "●" },
  WAITING:   { color: "#9ca3af", bg: "#f9fafb", border: "#e5e7eb", label: "Waiting",   icon: "○" },
  IN_REVIEW: { color: "#2563eb", bg: "#eff6ff", border: "#dbeafe", label: "In Review", icon: "◎" },
};

/* ══════════════════════════════════════════
   CHAIN MODAL
══════════════════════════════════════════ */
/**
 * Collapse per-auditor chain entries into one row per (auditorType, auditLevel).
 * A level shared by several external auditors (OR review) shows all their names
 * on a single row; the level's status is the strongest among them (a lone
 * approval clears the level).
 */
const groupChainLevels = (levels) => {
  const rank = (s) => ({ APPROVED: 4, ACCEPTED: 4, REJECTED: 3, PENDING: 2, IN_REVIEW: 2, WAITING: 1 }[(s || "").toUpperCase()] || 0);
  const byKey = new Map();
  const order = [];
  for (const lvl of levels) {
    const key = `${(lvl.auditorType || "").toUpperCase()}_${lvl.auditLevel}`;
    if (!byKey.has(key)) { byKey.set(key, { ...lvl, auditorNames: [] }); order.push(key); }
    const g = byKey.get(key);
    const name = lvl.onBehalf ? (lvl.auditorName || "Admin") : (lvl.auditorName || `Auditor #${lvl.auditorId || "—"}`);
    if (!g.auditorNames.includes(name)) g.auditorNames.push(name);
    if (rank(lvl.status) > rank(g.status)) g.status = lvl.status;
    if (lvl.onBehalf) g.onBehalf = true;
  }
  return order.map((k) => {
    const g = byKey.get(k);
    return { ...g, auditorName: g.auditorNames.join(", ") };
  });
};

export const ChainModal = ({
  auditLevels: rawAuditLevels = [],
  onClose,
  admin = false,
  saving = false,
  onApproveThrough,
  onReject,
}) => {
  const auditLevels = groupChainLevels(rawAuditLevels);
  useEffect(() => {
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose]);

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [showReject, setShowReject] = useState(false);

  const completed = auditLevels.filter(l => ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase())).length;

  const targets = admin ? selectableTargets(auditLevels) : [];
  const targetKeys = new Set(targets.map(t => `${(t.auditorType || "").toUpperCase()}_${t.auditLevel}`));
  const coveredFor = (auditLevel, auditorType) => {
    const idx = targets.findIndex(
      t => t.auditLevel === auditLevel && (t.auditorType || "").toUpperCase() === (auditorType || "").toUpperCase(),
    );
    return idx === -1 ? [] : targets.slice(0, idx + 1);
  };
  const coveredLabel = (list) => list.map(c => `${c.auditorName} (L${c.auditLevel})`).join(", ");

  const doApprove = async () => {
    if (!confirmTarget) return;
    try { await onApproveThrough?.(confirmTarget.auditLevel, confirmTarget.auditorType); onClose(); }
    catch { /* leave modal open on failure */ }
  };
  const doReject = async (note) => {
    try { await onReject?.(note); onClose(); }
    catch { /* leave modal open on failure */ }
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}
    >
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 480, boxShadow: "0 20px 60px rgba(0,0,0,.18)", overflow: "hidden", animation: "auditScaleIn .18s ease" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #f3f4f6" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Audit Chain</div>
            <div style={{ fontSize: 11.5, color: "#9ca3af", marginTop: 2 }}>
              {admin && targets.length > 0
                ? "Approve on behalf through any pending level, or reject."
                : `${completed} of ${auditLevels.length} level${auditLevels.length !== 1 ? "s" : ""} completed`}
            </div>
          </div>
          <button onClick={onClose} style={{ width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e7eb", borderRadius: 7, background: "transparent", cursor: "pointer", fontSize: 18, color: "#6b7280", lineHeight: 1 }}>×</button>
        </div>

        <div style={{ height: 3, background: "#f3f4f6" }}>
          <div style={{ height: "100%", width: `${auditLevels.length ? (completed / auditLevels.length) * 100 : 0}%`, background: completed === auditLevels.length ? "#16a34a" : "#2563eb", transition: "width .4s ease" }} />
        </div>

        <div style={{ padding: "4px 0 6px", maxHeight: "52vh", overflowY: "auto" }}>
          {auditLevels.map((lvl, i) => {
            const sc = CHAIN_SC[(lvl.status || "WAITING").toUpperCase()] || CHAIN_SC.WAITING;
            const isExt = (lvl.auditorType || "").toUpperCase() === "EXTERNAL";
            const isLast = i === auditLevels.length - 1;
            const isTarget = admin && targetKeys.has(`${(lvl.auditorType || "").toUpperCase()}_${lvl.auditLevel}`);
            return (
              <div key={i}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "12px 20px" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
                    <div style={{
                      width: 34, height: 34, borderRadius: "50%",
                      background: sc.bg, border: `2px solid ${sc.border}`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 11, fontWeight: 800, color: sc.color,
                    }}>
                      {["APPROVED", "ACCEPTED"].includes((lvl.status || "").toUpperCase()) ? "✓" : `L${lvl.auditLevel}`}
                    </div>
                    {!isLast && <div style={{ width: 2, height: 24, background: "#f3f4f6", marginTop: 3 }} />}
                  </div>

                  <div style={{ flex: 1, minWidth: 0, paddingTop: 4 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                        Level {lvl.auditLevel}
                      </span>
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                        background: isExt ? "#f5f3ff" : "#eff6ff", color: isExt ? "#7c3aed" : "#2563eb",
                        border: `1px solid ${isExt ? "#ddd6fe" : "#dbeafe"}`,
                      }}>
                        {isExt ? "EXTERNAL" : "INTERNAL"}
                      </span>
                      {lvl.onBehalf && (
                        <span style={{
                          fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                          background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
                        }}>
                          ON BEHALF
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 12.5, color: "#374151", fontWeight: 500 }}>
                      {lvl.onBehalf
                        ? `${lvl.auditorName || "Admin"} (Admin)`
                        : (lvl.auditorName || `Auditor #${lvl.auditorId || "—"}`)}
                    </div>
                    {lvl.onBehalf && lvl.coveredLevels?.length > 0 && (
                      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 3 }}>
                        on behalf of {coveredLabel(lvl.coveredLevels)}
                      </div>
                    )}
                    {lvl.auditedDate && (
                      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>
                        Actioned: {fmtDate(lvl.auditedDate)}
                      </div>
                    )}
                    {lvl.remark && (
                      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 5, padding: "6px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f3f4f6", fontStyle: "italic" }}>
                        "{lvl.remark}"
                      </div>
                    )}
                  </div>

                  <div style={{ flexShrink: 0, paddingTop: 6 }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      padding: "4px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
                      background: sc.bg, color: sc.color, border: `1.5px solid ${sc.border}`,
                    }}>
                      <span style={{ fontWeight: 800 }}>{sc.icon}</span> {sc.label}
                    </span>
                  </div>
                </div>
                {isTarget && !confirmTarget && !showReject && (
                  <div style={{ padding: "0 20px 10px 68px" }}>
                    <button
                      className="audit-btn-approve"
                      onClick={() => setConfirmTarget({ auditLevel: lvl.auditLevel, auditorType: lvl.auditorType })}
                      disabled={saving}
                      style={{ fontSize: 11.5, padding: "4px 12px" }}
                    >
                      ✓ Approve through here
                    </button>
                  </div>
                )}
                {!isLast && <div style={{ height: 1, background: "#f9fafb", margin: "0 20px" }} />}
              </div>
            );
          })}
        </div>

        {admin && confirmTarget && (
          <div style={{ padding: "12px 18px", borderTop: "1px solid #f3f4f6", background: "#f0fdf4" }}>
            <div style={{ fontSize: 12.5, color: "#374151", lineHeight: 1.55, marginBottom: 9 }}>
              Record one admin approval on behalf of {coveredLabel(coveredFor(confirmTarget.auditLevel, confirmTarget.auditorType))}. Levels after this remain with their assigned auditors.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="audit-btn-secondary" onClick={() => setConfirmTarget(null)} disabled={saving} style={{ fontSize: 13 }}>Cancel</button>
              <button className="audit-btn-approve" onClick={doApprove} disabled={saving} style={{ fontSize: 13, minWidth: 110, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 6 }}>
                {saving
                  ? <><span className="audit-spinner" style={{ width: 11, height: 11, borderWidth: 1.5, borderTopColor: "currentColor", borderColor: "rgba(0,0,0,.12)" }} /> Saving…</>
                  : "✓ Approve"}
              </button>
            </div>
          </div>
        )}

        {admin && showReject && (
          <InlineReject asRow={false} saving={saving} onConfirm={doReject} onCancel={() => setShowReject(false)} />
        )}

        <div style={{ padding: "12px 18px", borderTop: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
          {admin && targets.length > 0 && !confirmTarget && !showReject && (
            <button className="audit-btn-reject" onClick={() => setShowReject(true)} disabled={saving} style={{ fontSize: 13, marginRight: "auto" }}>✕ Reject on behalf</button>
          )}
          <button className="audit-btn-secondary" onClick={onClose} style={{ fontSize: 13 }}>Close</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Chain trigger pill. Counts distinct levels, not per-auditor rows — a level
 * shared by several external auditors is one step, complete once any approves.
 */
export const ChainButton = ({ auditLevels = [], admin = false, saving = false, onApproveThrough, onReject }) => {
  const [open, setOpen] = useState(false);
  if (!auditLevels.length) return null;
  const grouped = groupChainLevels(auditLevels);
  const completed = grouped.filter(l => ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase())).length;
  const total = grouped.length;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          padding: "3px 10px", borderRadius: 20, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
          border: admin ? "1px solid #bbf7d0" : "1px solid #e5e7eb",
          background: admin ? "#f0fdf4" : "#f9fafb",
          color: admin ? "#16a34a" : "#6b7280",
          display: "inline-flex", alignItems: "center", gap: 5, transition: "all .15s",
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: completed === total ? "#16a34a" : "#d97706", flexShrink: 0 }} />
        {admin ? "Audit chain" : `Chain ${completed}/${total}`}
      </button>
      {open && (
        <ChainModal
          auditLevels={auditLevels}
          onClose={() => setOpen(false)}
          admin={admin}
          saving={saving}
          onApproveThrough={onApproveThrough}
          onReject={onReject}
        />
      )}
    </>
  );
};

/* ── Logic helpers ── */
export const resolveMyEntry = (auditLevels = [], currentUserId) =>
  // eslint-disable-next-line eqeqeq
  auditLevels.find(l => l.auditorId == currentUserId) || null;

/**
 * Ordered list of levels an admin may approve THROUGH, on behalf of the
 * assigned auditors — the current pending front and every level after it
 * (status PENDING / IN_REVIEW / WAITING), canonically ordered INTERNAL then
 * EXTERNAL, ascending by level. Already-actioned levels (APPROVED/REJECTED) are
 * excluded. Pure — mirrors the backend collapse ordering.
 */
export const selectableTargets = (auditLevels = []) => {
  const typeOrder = t => ((t || "").toUpperCase() === "EXTERNAL" ? 1 : 0);
  return [...auditLevels]
    .sort((a, b) => typeOrder(a.auditorType) - typeOrder(b.auditorType) || (a.auditLevel - b.auditLevel))
    .filter(l => ["PENDING", "IN_REVIEW", "WAITING"].includes((l.status || "").toUpperCase()))
    .map(l => ({
      auditLevel: l.auditLevel,
      auditorType: l.auditorType,
      auditorName: l.auditorName || `Auditor #${l.auditorId || "—"}`,
    }));
};

/**
 * Whether every level before the user's own is cleared. Under OR review a level
 * is cleared once ANY of its auditors approves, so prior rows are grouped by
 * (type, level) and each must have at least one APPROVED/ACCEPTED — not every
 * row — staying correct even if hydration leaves a cleared level's non-acting
 * auditors pending.
 * @param {Array} auditLevels the answer's audit chain (per-auditor entries).
 * @param {object|null} myEntry the current user's chain entry.
 * @returns {boolean}
 */
export const prevLevelsAllApproved = (auditLevels = [], myEntry) => {
  if (!myEntry) return false;
  const myType = (myEntry.auditorType || "").toUpperCase();
  const toCheck = myType === "EXTERNAL"
    ? auditLevels.filter(l => (l.auditorType || "").toUpperCase() === "INTERNAL" || ((l.auditorType || "").toUpperCase() === "EXTERNAL" && l.auditLevel < myEntry.auditLevel))
    : auditLevels.filter(l => (l.auditorType || "").toUpperCase() === "INTERNAL" && l.auditLevel < myEntry.auditLevel);
  const clearedByLevel = new Map();
  for (const l of toCheck) {
    const key = `${(l.auditorType || "").toUpperCase()}_${l.auditLevel}`;
    const approved = ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase());
    clearedByLevel.set(key, (clearedByLevel.get(key) || false) || approved);
  }
  return [...clearedByLevel.values()].every(Boolean);
};

/**
 * Single source of truth for "may I act on this answer" from its chain snapshot.
 * A level shared by several external auditors (OR review) stores only one of them
 * as current_auditor_id, so eligibility is: the answer is still open, my own
 * entry is pending at the live front (my level, prior levels approved), and no
 * co-auditor has already cleared my level. Any of these failing hides the
 * Approve/Reject controls.
 * @param {Array} auditLevels the answer's audit chain (per-auditor entries).
 * @param {object|null} myEntry the current user's chain entry (resolveMyEntry).
 * @param {string} answerStatus the answer's overall audit status.
 * @returns {boolean}
 */
export const canActOnEntry = (auditLevels = [], myEntry, answerStatus) => {
  if (!myEntry) return false;
  const myStatus = (myEntry.status || "PENDING").toUpperCase();
  if (!["PENDING", "IN_REVIEW"].includes(myStatus)) return false;
  if (!["PENDING", "IN_REVIEW"].includes((answerStatus || "PENDING").toUpperCase())) return false;
  if (!prevLevelsAllApproved(auditLevels, myEntry)) return false;
  const myType = (myEntry.auditorType || "").toUpperCase();
  const levelCleared = auditLevels.some(l =>
    Number(l.auditLevel) === Number(myEntry.auditLevel) &&
    (l.auditorType || "").toUpperCase() === myType &&
    ["APPROVED", "ACCEPTED"].includes((l.status || "").toUpperCase()));
  return !levelCleared;
};

/**
 * Badge text for a level the current user has already acted on — distinguishing
 * a self-action from an admin approve-on-behalf.
 * @param {string} myStatus the user's entry status.
 * @param {boolean} onBehalf whether the level was approved on their behalf.
 * @returns {string}
 */
export const actedLabel = (myStatus, onBehalf) =>
  (myStatus || "").toUpperCase() === "REJECTED"
    ? "✕ Rejected by you"
    : onBehalf ? "✓ Approved on your behalf" : "✓ Approved by you";

/* ══════════════════════════════════════════
   CONFIRM MODAL — used by approve flows
══════════════════════════════════════════ */
export const ConfirmModal = ({ title, message, confirmLabel = "Confirm", confirmCls = "audit-btn-approve", onConfirm, onCancel, saving }) => (
  <div
    onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    style={{ position: "fixed", inset: 0, zIndex: 2100, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(3px)" }}
  >
    <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 380, boxShadow: "0 16px 40px rgba(0,0,0,.14)", overflow: "hidden" }}>
      <div style={{ padding: "16px 18px 14px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: "#6b7280", lineHeight: 1.6 }}>{message}</div>
      </div>
      <div style={{ padding: "12px 18px 16px", display: "flex", gap: 8, justifyContent: "flex-end", borderTop: "1px solid #f3f4f6" }}>
        <button className="audit-btn-secondary" onClick={onCancel} disabled={saving} style={{ fontSize: 13 }}>Cancel</button>
        <button
          className={confirmCls}
          onClick={onConfirm}
          disabled={saving}
          style={{ fontSize: 13, minWidth: 110, justifyContent: "center", display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {saving
            ? <><span className="audit-spinner" style={{ width: 11, height: 11, borderWidth: 1.5, borderTopColor: "currentColor", borderColor: "rgba(0,0,0,.12)" }} /> Saving…</>
            : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

/* ══════════════════════════════════════════
   DOC MODAL — view a single doc or paginate a list.
   Handles old (string / { url }) and new ({ fileMetadata, documentMetadata })
   payload shapes from the backend.
══════════════════════════════════════════ */
export const extractDocInfo = (doc) => {
  if (!doc) return { url: "", name: "File", mimeType: "" };
  const fm = doc.fileMetadata || {};
  const dm = doc.documentMetadata || {};
  return {
    url:      fm.url      || dm.url      || doc.url      || "",
    name:     fm.fileName || dm.name     || doc.name     || dm.fileName || "Document",
    mimeType: fm.mimeType || doc.mimeType || "",
  };
};

export const DocModal = ({ docs, onClose }) => {
  const allDocs = Array.isArray(docs) ? docs : [docs];
  const [idx, setIdx] = useState(0);
  const total = allDocs.length;
  const { url, name, mimeType } = extractDocInfo(allDocs[idx]);
  const isImage = /image\//i.test(mimeType) || /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(url);
  const isPdf   = /pdf/i.test(mimeType)     || /\.pdf(\?|$)/i.test(url);

  useEffect(() => {
    const h = e => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") setIdx(i => Math.max(0, i - 1));
      else if (e.key === "ArrowRight") setIdx(i => Math.min(total - 1, i + 1));
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onClose, total]);

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.45)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "100%", maxWidth: 860, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 18px", borderBottom: "1px solid #e5e7eb", flexShrink: 0 }}>
          {total > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              <button
                onClick={() => setIdx(i => Math.max(0, i - 1))} disabled={idx === 0}
                style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e5e7eb", background: idx === 0 ? "#f9fafb" : "#fff", cursor: idx === 0 ? "default" : "pointer", fontSize: 13, color: idx === 0 ? "#d1d5db" : "#374151", lineHeight: 1 }}
              >‹</button>
              <span style={{ fontSize: 11.5, fontWeight: 600, color: "#6b7280", minWidth: 40, textAlign: "center" }}>{idx + 1} / {total}</span>
              <button
                onClick={() => setIdx(i => Math.min(total - 1, i + 1))} disabled={idx === total - 1}
                style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid #e5e7eb", background: idx === total - 1 ? "#f9fafb" : "#fff", cursor: idx === total - 1 ? "default" : "pointer", fontSize: 13, color: idx === total - 1 ? "#d1d5db" : "#374151", lineHeight: 1 }}
              >›</button>
              <div style={{ width: 1, height: 16, background: "#e5e7eb" }} />
            </div>
          )}
          <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {url && <a href={url} target="_blank" rel="noreferrer" style={{ padding: "4px 12px", borderRadius: 6, border: "1px solid #e5e7eb", background: "#f9fafb", color: "#374151", fontSize: 12, textDecoration: "none", fontWeight: 500 }}>Open in tab</a>}
            <button onClick={onClose} style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #e5e7eb", background: "transparent", cursor: "pointer", fontSize: 18, color: "#6b7280", lineHeight: 1 }}>×</button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 18, background: "#f9fafb", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 240 }}>
          {isImage && <img src={url} alt={name} style={{ maxWidth: "100%", maxHeight: "68vh", borderRadius: 8 }} />}
          {isPdf && <iframe src={url} title={name} style={{ width: "100%", height: "68vh", border: "none", borderRadius: 8 }} />}
          {!isImage && !isPdf && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 36, opacity: .18, marginBottom: 12 }}>📄</div>
              <div style={{ fontWeight: 600, color: "#374151", marginBottom: 10 }}>Preview unavailable</div>
              {url && <a href={url} target="_blank" rel="noreferrer" style={{ padding: "6px 16px", borderRadius: 7, background: "#2563eb", color: "#fff", fontSize: 12.5, fontWeight: 600, textDecoration: "none" }}>Download</a>}
            </div>
          )}
        </div>
        {total > 1 && (
          <div style={{ display: "flex", gap: 6, padding: "10px 18px", borderTop: "1px solid #e5e7eb", background: "#f9fafb", overflowX: "auto", flexShrink: 0 }}>
            {allDocs.map((d, i) => {
              const { name: n } = extractDocInfo(d);
              return (
                <button
                  key={i} onClick={() => setIdx(i)}
                  style={{ padding: "5px 12px", borderRadius: 7, border: `1.5px solid ${i === idx ? "#2563eb" : "#e5e7eb"}`, background: i === idx ? "#eff6ff" : "#fff", color: i === idx ? "#2563eb" : "#374151", fontSize: 11, fontWeight: i === idx ? 700 : 500, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}
                >
                  {i + 1}. {n.length > 20 ? n.slice(0, 18) + "…" : n}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   BULK REJECT MODAL — shared rejection reason
   for all selected answers.
══════════════════════════════════════════ */
export const BulkRejectModal = ({ count, onConfirm, onCancel, saving }) => {
  const [note, setNote] = useState("");
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onCancel(); }} style={{ position: "fixed", inset: 0, zIndex: 2000, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, backdropFilter: "blur(4px)" }}>
      <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 420, boxShadow: "0 20px 50px rgba(0,0,0,.15)" }}>
        <div style={{ padding: "15px 18px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Reject {count} Answer{count !== 1 ? "s" : ""}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>Reason applies to all selected answers.</div>
        </div>
        <div style={{ padding: "16px 18px" }}>
          <label className="audit-label">Reason *</label>
          <textarea autoFocus rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Explain the rejection…" className="audit-input" style={{ resize: "none", fontSize: 13, lineHeight: 1.55 }} />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => note.trim() && onConfirm(note)} disabled={!note.trim() || saving} className="audit-btn-reject" style={{ flex: 1, justifyContent: "center" }}>
              {saving ? "Saving…" : "Confirm Rejection"}
            </button>
            <button onClick={onCancel} className="audit-btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════
   INLINE REJECT — reason input + confirm/cancel.
   Used as a table row in AuditAnswer (the colSpan
   prop is configurable so callers can drop it into
   a non-table layout too — pass colSpan={undefined}).
══════════════════════════════════════════ */
export const InlineReject = ({ onConfirm, onCancel, saving, asRow = true, colSpan = 11 }) => {
  const [note, setNote] = useState("");
  const body = (
    <div style={{ padding: "12px 18px 14px 56px", background: "#fef2f2", borderTop: "1px dashed #fecaca", borderBottom: "1px solid #fecaca" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: ".07em", marginBottom: 7 }}>Rejection reason *</div>
      <textarea
        autoFocus rows={2} value={note} onChange={e => setNote(e.target.value)}
        placeholder="Explain why this answer is being rejected…"
        style={{ width: "100%", maxWidth: 500, resize: "none", fontSize: 12.5, padding: "8px 11px", borderRadius: 7, border: "1px solid #fca5a5", outline: "none", fontFamily: "inherit", color: "#111827", background: "#fff" }}
      />
      <div style={{ display: "flex", gap: 7, marginTop: 9 }}>
        <button onClick={() => note.trim() && onConfirm(note)} disabled={!note.trim() || saving} className="audit-btn-reject" style={{ fontSize: 12, padding: "5px 14px" }}>
          {saving ? "Saving…" : "Confirm"}
        </button>
        <button onClick={onCancel} className="audit-btn-secondary" style={{ fontSize: 12, padding: "5px 12px" }}>Cancel</button>
      </div>
    </div>
  );
  if (!asRow) return body;
  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>{body}</td>
    </tr>
  );
};
